import { useEffect, useState } from "react";

import { IgDialog, IgTextField } from "@intelligems/ig-theme";
import Stack from "@mui/material/Stack";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction,
  ExperienceBuilderSelector,
} from "@src/store/slices/experienceBuilder";
import { isValidDemoUrl } from "@src/store/slices/experienceBuilder/common/utils/isValidDemoUrl";

interface EditDemoUrlDialogProps {
  open: boolean;
  onClose: () => void;
}

export const EditDemoUrlDialog = ({
  open,
  onClose,
}: EditDemoUrlDialogProps) => {
  const dispatch = useDispatch();
  const demoUrl = useSelector(ExperienceBuilderSelector.getDemoUrl);

  const [url, setUrl] = useState("");

  // Re-seed from the current demo URL each time the dialog opens.
  useEffect(() => {
    if (open) {
      setUrl(demoUrl ?? "");
    }
  }, [open, demoUrl]);

  const trimmed = url.trim();
  const changed = trimmed !== (demoUrl ?? "");
  // Allow applying a valid URL, or an empty value to clear an existing demo URL.
  const canApply = changed && (trimmed === "" || isValidDemoUrl(trimmed));

  const handleApply = () => {
    if (!canApply) {
      return;
    }
    void dispatch(ExperienceBuilderAction.setDemoUrl(url));
    onClose();
  };

  return (
    <IgDialog
      title="Edit demo URL"
      showDialog={open}
      handleClose={onClose}
      showCloseIcon
      maxWidth="sm"
      dialogHeight={null}
      dontUseDialogContent
      yesButtonText="Apply"
      noButtonText="Cancel"
      handleYesClick={handleApply}
      handleNoClick={onClose}
      yesButtonDisabled={!canApply}
      rawContent={
        <Stack spacing={2}>
          <IgTextField
            fullWidth
            size="small"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/products/foo"
            label="Demo URL"
          />
        </Stack>
      }
    />
  );
};
